
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Search,
  Download,
  FileText,
  TrendingUp,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { financialReportsService, FinancialReportItem } from '@/services/financialReports.service';
import { invoiceService } from '@/services/invoice.service';
import { getCurrencySymbol } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';

export default function Reports() {
  const [transactions, setTransactions] = useState<FinancialReportItem[]>([]);
  const [allTransactions, setAllTransactions] = useState<FinancialReportItem[]>([]);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [total, setTotal] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [invoiceOutstanding, setInvoiceOutstanding] = useState<number>(0);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const { toast } = useToast();

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const handleExportReport = async () => {
    try {
      setIsExporting(true);
      const download = await financialReportsService.exportPdf({ search: search || undefined });
      const blobUrl = window.URL.createObjectURL(download.blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', download.fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast({
        title: 'Download started',
        description: 'The financial report is being saved as a PDF.',
      });
    } catch (error) {
      const description =
        error instanceof Error && error.message.trim()
          ? error.message
          : 'Unable to export the financial report right now.';
      console.error('Financial report export failed', error);
      toast({
        variant: 'destructive',
        title: 'Export failed',
        description,
      });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setIsRefreshing(true);
        const resp = await financialReportsService.list({ page, limit: pageSize, search: search || undefined });
        setTransactions(resp.data);
        setTotal(resp.total);
      } finally {
        setIsRefreshing(false);
      }
    };
    load();
  }, [page, pageSize, search]);

  useEffect(() => {
    const loadAllForStats = async () => {
      try {
        const resp = await financialReportsService.list({ limit: 10000, search: search || undefined });
        setAllTransactions(resp.data);
      } catch (error) {
        console.error('Failed to load all transactions for stats', error);
      }
    };
    loadAllForStats();
  }, [search]);

  useEffect(() => {
    const loadOutstanding = async () => {
      try {
        const response = await invoiceService.getInvoices({ limit: 1000, search: search || undefined });
        // Filter invoices by selected currency
        const filteredInvoices = response.data.filter((invoice) => 
          invoice.currency?.toUpperCase() === selectedCurrency.toUpperCase()
        );
        const totalOutstanding = filteredInvoices.reduce((sum, invoice) => {
          const status = (invoice.status || '').toLowerCase();
          if (status === 'pending' || status === 'overdue') {
            const baseAmount =
              typeof invoice.subtotal === 'number'
                ? invoice.subtotal
                : typeof invoice.totalAmount === 'number'
                  ? invoice.totalAmount
                  : Number(invoice.totalAmount ?? 0);
            return Number.isFinite(baseAmount) ? sum + baseAmount : sum;
          }
          return sum;
        }, 0);
        setInvoiceOutstanding(totalOutstanding);
      } catch {
        setInvoiceOutstanding(0);
      }
    };
    loadOutstanding();
  }, [search, selectedCurrency]);

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    const locale = currency?.toUpperCase() === 'INR' ? 'en-IN' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  const stats = useMemo(() => {
    // Filter transactions by selected currency
    const filteredTransactions = allTransactions.filter((t) => {
      const tType = (t.type || '').toLowerCase();
      const originalCurrency = (t as { currency?: string }).currency;
      const resolvedCurrency =
        tType === 'expense'
          ? 'INR'
          : (originalCurrency && originalCurrency.trim()) || 'INR';
      return resolvedCurrency.toUpperCase() === selectedCurrency.toUpperCase();
    });

    const income = filteredTransactions
      .filter(t => (t.type || '').toLowerCase() === 'income')
      .reduce((sum, t) => sum + Math.abs(Number((t.subtotal ?? t.amount) || 0)), 0);
    const expenses = filteredTransactions
      .filter(t => (t.type || '').toLowerCase() === 'expense')
      .reduce((sum, t) => sum + Math.abs(Number((t.subtotal ?? t.amount) || 0)), 0);
    const profit = income - expenses;
    return {
      revenue: { current: income, change: 0 },
      expenses: { current: expenses, change: 0 },
      profit: { current: profit, change: 0 },
      outstanding: { current: invoiceOutstanding, change: 0 },
    };
  }, [allTransactions, invoiceOutstanding, selectedCurrency]);
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Financial Reports</h1>
          <p className="text-muted-foreground">
            View and analyze your financial data
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
            <SelectTrigger className="w-[120px] h-10 border-gray-300">
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD $</SelectItem>
              <SelectItem value="EUR">EUR €</SelectItem>
              <SelectItem value="GBP">GBP £</SelectItem>
              <SelectItem value="INR">INR ₹</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            className="h-10 rounded-lg"
            onClick={handleExportReport}
            disabled={isExporting}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {isExporting ? 'Preparing...' : 'Export Report'}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <span className="text-lg font-semibold text-muted-foreground">
              {getCurrencySymbol(selectedCurrency)}
            </span>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.revenue.current, selectedCurrency)}</div>
            <div className="flex items-center text-xs text-muted-foreground">
              {stats.revenue.change > 0 ? (
                <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-red-500 mr-1" />
              )}
              {Math.abs(stats.revenue.change)}% from last month
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Expenses</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.expenses.current, selectedCurrency)}</div>
            <div className="flex items-center text-xs text-muted-foreground">
              {stats.expenses.change > 0 ? (
                <ArrowUpRight className="h-4 w-4 text-red-500 mr-1" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-green-500 mr-1" />
              )}
              {Math.abs(stats.expenses.change)}% from last month
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Profit</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.profit.current, selectedCurrency)}</div>
            <div className="flex items-center text-xs text-muted-foreground">
              {stats.profit.change > 0 ? (
                <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-red-500 mr-1" />
              )}
              {Math.abs(stats.profit.change)}% from last month
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Outstanding</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.outstanding.current, selectedCurrency)}</div>
            <div className="flex items-center text-xs text-muted-foreground">
              {stats.outstanding.change > 0 ? (
                <ArrowUpRight className="h-4 w-4 text-red-500 mr-1" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-green-500 mr-1" />
              )}
              {Math.abs(stats.outstanding.change)}% from last month
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Transactions</CardTitle>
              <CardDescription>Latest financial transactions</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search transactions..." 
                  className="pl-8"
                  value={search}
                  onChange={(e) => {
                    setPage(1);
                    setSearch(e.target.value);
                  }}
                />
              </div>
              <Select
                value={pageSize.toString()}
                onValueChange={(v) => {
                  setPageSize(parseInt(v, 10));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Rows per page" />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 25, 50].map((sz) => (
                    <SelectItem key={sz} value={sz.toString()}>
                      {sz} rows
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table className="min-w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
              {isRefreshing && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6">Loading...</TableCell>
                </TableRow>
              )}
              {!isRefreshing && transactions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6">No transactions found</TableCell>
                </TableRow>
              )}
              {!isRefreshing && transactions.map((transaction) => {
                const tType = (transaction.type || '').toLowerCase();
                const amount = Number((transaction.subtotal ?? transaction.amount) || 0);
                const sign = tType === 'income' ? '+' : '-';
                const color = tType === 'income' ? 'text-green-500' : 'text-red-500';
                const originalCurrency = (transaction as { currency?: string }).currency;
                const resolvedCurrency =
                  tType === 'expense'
                    ? 'INR'
                    : (originalCurrency && originalCurrency.trim()) || 'INR';
                const currencySymbol = getCurrencySymbol(resolvedCurrency);
                const locale = resolvedCurrency.toUpperCase() === 'INR' ? 'en-IN' : 'en-US';
                const key = (transaction.id || transaction._id || `${transaction.description}-${transaction.date}`);
                const displayAmount = tType === 'expense' ? Math.abs(amount) : amount;
                const formattedAmount = displayAmount.toLocaleString(locale, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
                return (
                  <TableRow key={key}>
                    <TableCell className="font-medium">{transaction.description}</TableCell>
                    <TableCell>{transaction.category || '-'}</TableCell>
                    <TableCell className={color}>
                      {sign}{currencySymbol}{formattedAmount}
                    </TableCell>
                    <TableCell>{formatDate(transaction.date)}</TableCell>
                    <TableCell>
                      <span className={`capitalize ${color}`}>
                        {transaction.type || '-'}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
              </TableBody>
            </Table>
          </div>
          {!isRefreshing && transactions.length > 0 && (
          <div className="flex items-center justify-between pt-4">
            <div className="text-sm text-muted-foreground">
              Showing {(transactions.length === 0 ? 0 : (page - 1) * pageSize) + (transactions.length ? 1 : 0)} to {Math.min(page * pageSize, total)} of {total} entries
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-xl"
              >
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {(() => {
                  const pageButtons: JSX.Element[] = [];
                  
                  if (totalPages > 3) {
                    let startPage = Math.max(1, page - 1);
                    let endPage = Math.min(totalPages, page + 1);
                    
                    if (startPage === 1) {
                      endPage = Math.min(totalPages, 3);
                    }
                    if (endPage === totalPages) {
                      startPage = Math.max(1, totalPages - 2);
                    }
                    
                    if (startPage > 1) {
                      pageButtons.push(
                        <Button
                          key={1}
                          variant={1 === page ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPage(1)}
                          className="rounded-xl w-8 h-8 p-0"
                        >
                          1
                        </Button>
                      );
                      if (startPage > 2) {
                        pageButtons.push(
                          <span key="ellipsis-start" className="px-2 text-muted-foreground">
                            ..
                          </span>
                        );
                      }
                    }
                    
                    for (let i = startPage; i <= endPage; i++) {
                      pageButtons.push(
                        <Button
                          key={i}
                          variant={i === page ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPage(i)}
                          className="rounded-xl w-8 h-8 p-0"
                        >
                          {i}
                        </Button>
                      );
                    }
                    
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pageButtons.push(
                          <span key="ellipsis-end" className="px-2 text-muted-foreground">
                            ..
                          </span>
                        );
                      }
                      pageButtons.push(
                        <Button
                          key={totalPages}
                          variant={totalPages === page ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPage(totalPages)}
                          className="rounded-xl w-8 h-8 p-0"
                        >
                          {totalPages}
                        </Button>
                      );
                    }
                  } else {
                    for (let i = 1; i <= totalPages; i++) {
                      pageButtons.push(
                        <Button
                          key={i}
                          variant={i === page ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPage(i)}
                          className="rounded-xl w-8 h-8 p-0"
                        >
                          {i}
                        </Button>
                      );
                    }
                  }
                  
                  return pageButtons;
                })()}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="rounded-xl"
              >
                Next
              </Button>
            </div>
          </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 